import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  Button,
  Typography,
  SelectChangeEvent,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import ContractFormTableComponent from "../ContractForm/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ClearIcon from "@mui/icons-material/Clear";
import { validateNonZeroFloat, validateNonZeroInteger } from "../../Helpers/field.helper";
// Type definitions
interface OptionType {
  value: string;
  label: string;
}

interface FieldDependency {
  disabled: boolean;
}

interface TouchedType {
  [key: string]: boolean;
}

interface ErrorsType {
  [key: string]: string;
}
interface FieldDependency {
  disabled: boolean;
  nonSelectable?: boolean; // New property to control pointerEvents
}
interface FormDataType {
  [key: string]: string | OptionType[];
  derivative: string;
  singlDiff: string;
  majorMini: string;
  fullBal: string;
  contract: string;
  contract1: string;
  contract2: string;
  majorMiConn: string;
  balMontConn: string;
  unit: string;
  tick: string;
  holiday: string;
  bblConv: string;
  price_unit: string;
  priceConversionValue: string;
  fwMonth: string;
  ExchangeFees: string;
  ExchangeClearanceFees: string;
  exchangefeeBlocked: string;
  exchangeClearanceFeesBlocked: string;
  screen: string;
  iceClearEuScreenFee: string;
  iceClearEuBlockFee: string;
  cashSettle: string;
  gmifcCode: string;
  physical: string;
  logical: string;
  symbol: string;
  plattsCode: string;
  contractfuturesequivalent: string;
}

interface FieldDependenciesType {
  [key: string]: FieldDependency;
}

interface CompactSelectProps {
  label: string;
  options: OptionType[];
  value: string;
  onChange: (event: SelectChangeEvent) => void;
  disabled?: boolean;
  sx?: object;
  slotProps?: {
    inputLabel?: {
      shrink?: boolean;
    };
    htmlInput?: {
      [key: string]: any;
    };
  };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
}

interface CompactMultiSelectProps {
  label: string;
  options: OptionType[];
  value: OptionType[];
  onChange: (event: React.SyntheticEvent, value: OptionType[]) => void;
  disabled?: boolean;
  sx?: object;
}

interface CompactTextFieldProps {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  sx?: object;
  slotProps?: {
    inputLabel?: {
      shrink?: boolean;
    };
    htmlInput?: {
      [key: string]: any;
    };
  };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
}

// Ultra compact select component
const CompactSelect: React.FC<CompactSelectProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  sx,
  slotProps,
  onBlur,
  error = false,
  helperText,
}) => {
  const sortedOptions = [...options].sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  // Dynamic label props based on whether a value is selected
  const dynamicLabelProps = value
    ? { shrink: true, ...slotProps?.inputLabel }
    : slotProps?.inputLabel || {};

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      sx={{
        ...sx,
        flex: {
          xs: "1 1 auto",
          sm: "1 1 25%",
          md: "1 1 25%",
          lg: "1 1 25%",
          xl: "1 1 12%",
        },
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.9rem",
          transform: "translate(14px, 8px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -8px) scale(0.74)",
            backgroundColor: "white",
            paddingRight: "0.4rem",
            fontWeight: "bold",
            color: error ? "#d32f2f" : "black",
            letterSpacing: "normal",
          },
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "#d32f2f" : "#b9baeb85",
          borderWidth: "2px",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#999",
          opacity: 1,
        },
        "& .MuiInputBase-input[type='date']": {
          color: value ? "inherit" : "#999",
          "&::-webkit-datetime-edit": {
            color: value ? "inherit" : "#999",
          },
          "&::-webkit-datetime-edit-fields-wrapper": {
            color: value ? "inherit" : "##999",
          },
        },
      }}
      disabled={disabled}
    >
      <InputLabel {...dynamicLabelProps}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={onChange}
        onBlur={onBlur}
        displayEmpty
        sx={{
          "& .MuiSelect-select": {
            py: 0.5,
            color: value ? "inherit" : "#999",
          },
        }}
      >
        {!value && (
          <MenuItem
            value=""
            disabled
            sx={{ fontSize: "0.7rem", color: "#999" }}
          >
            Select
          </MenuItem>
        )}
        {sortedOptions.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ fontSize: "0.7rem" }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontSize: "0.65rem", margin: "3px 0 0 0" }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// Ultra compact text field component
const CompactTextField: React.FC<CompactTextFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  sx,
  slotProps,
  error = false,
  helperText,
  onBlur,
}) => {
  // Handle keypress to prevent 'e' in number inputs
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number" && (event.key === "e" || event.key === "E" || event.key === "-" || event.key === "+" )) {
      event.preventDefault();
    }
  };

  // Optional: Validate input to allow only digits (and optionally decimals)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (type === "number") {
      // Allow digits and optionally a decimal point; block 'e'
      if (/^\d*\.?\d*$/.test(inputValue) || inputValue === "") {
        onChange(event);
      }
    } else {
      onChange(event);
    }
  };
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      type={type}
      disabled={disabled}
      InputLabelProps={{
        shrink: type === "date" ? true : slotProps?.inputLabel?.shrink,
      }}
      InputProps={{
        ...slotProps?.htmlInput,
        onKeyPress: handleKeyPress,
        inputMode: type === "number" ? "numeric" : undefined,
        sx: {
          fontSize: "0.7rem",
          height: "32px",
        },
      }}
      sx={{
        ...sx,
        flex: {
          xs: "1 1 auto",
          sm: "1 1 25%",
          md: "1 1 25%",
          lg: "1 1 25%",
          xl: "1 1 12%",
        },
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.9rem",
          transform: "translate(14px, 8px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -8px) scale(0.74)",
            backgroundColor: "white",
            paddingRight: "0.4rem",
            fontWeight: "bold",
            color: error ? "#d32f2f" : "black",
            letterSpacing: "normal",
          },
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "#d32f2f" : "#b9baeb85",
          borderWidth: "2px",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#999",
          opacity: 1,
        },
        "& .MuiInputBase-input[type='date']": {
          color: value ? "inherit" : "#999",
          "&::-webkit-datetime-edit": {
            color: value ? "inherit" : "#999",
          },
          "&::-webkit-datetime-edit-fields-wrapper": {
            color: value ? "inherit" : "##999",
          },
        },
      }}
    />
  );
};

const CompactForm: React.FC<any> = ({
  dataVersion,
  setDataVersion,
  copiedData,
}) => {
  const queryClient = useQueryClient();
  const [isFormModified, setIsFormModified] = useState(false);
  const [balMontConnOptions, setBalMontConnOptions] = useState<OptionType[]>(
    []
  );
  const [majorMiConnOptions, setMajorMiConnOptions] = useState<OptionType[]>(
    []
  );
  const [contractsOption, setContracts] = useState([]);
  const [balMontOptions, setBalMontOptions] = useState([]);
  const [
    contractFuturesEquivalentOptions,
    setContractFuturesEquivalentOptions,
  ] = useState<OptionType[]>([]); // New state for futures equivalent options
  const [touched, setTouched] = useState<TouchedType>({});
  const [errors, setErrors] = useState<ErrorsType>({});
  const [unitOption, setUnitOptions] = useState([]);

  const [holidayOption, setHolidayOption] = useState([]);

  const [formData, setFormData] = useState<FormDataType>({
    derivative: "",
    singlDiff: "",
    majorMini: "",
    fullBal: "",
    contract: "",
    contract1: "",
    contract2: "",
    majorMiConn: "",
    balMontConn: "",
    unit: "",
    tick: "",
    holiday: "",
    bblConv: "",
    price_unit: "",
    priceConversionValue: "",
    fwMonth: "",
    ExchangeFees: "",
    ExchangeClearanceFees: "",
    exchangefeeBlocked: "",
    exchangeClearanceFeesBlocked: "",
    screen: "",
    iceClearEuScreenFee: "",
    iceClearEuBlockFee: "",
    cashSettle: "",
    gmifcCode: "",
    physical: "",
    logical: "",
    symbol: "",
    plattsCode: "",
    contractfuturesequivalent: "",
  });

  const calculateFieldDependencies = (
    formData: FormDataType
  ): FieldDependenciesType => {
    const dependencies: FieldDependenciesType = {
      contractfuturesequivalent: { disabled: true },
      derivative: { disabled: false },
      singlDiff: { disabled: false },
      majorMini: { disabled: false },
      fullBal: { disabled: false },
      contract1: { disabled: false },
      contract2: { disabled: false },
      majorMiConn: { disabled: false },
      balMontConn: { disabled: false },
      ExchangeFees: { disabled: false },
      ExchangeClearanceFees: { disabled: false },
      iceClearEuBlockFee: { disabled: false },
      screen: { disabled: false },
      holiday: { disabled: false },
    };

    // Apply derivative dependencies
    if (formData.derivative === "futures") {
      dependencies.contract1 = { disabled: true };
      dependencies.contract2 = { disabled: true };
      dependencies.majorMiConn = { disabled: true };
      dependencies.balMontConn = { disabled: true };
      dependencies.ExchangeFees = { disabled: false };
      dependencies.iceClearEuBlockFee = { disabled: true };
      dependencies.ExchangeClearanceFees = { disabled: false };
      dependencies.screen = { disabled: true };
      dependencies.holiday = { disabled: false };
      dependencies.contractfuturesequivalent = { disabled: true };
    } else if (formData.derivative === "swaps") {
      dependencies.ExchangeFees = { disabled: true };
      dependencies.ExchangeClearanceFees = { disabled: true };
      dependencies.contract1 = { disabled: false };
      dependencies.contract2 = { disabled: false };
      dependencies.majorMiConn = { disabled: false };
      dependencies.balMontConn = { disabled: false };
      dependencies.iceClearEuBlockFee = { disabled: false };
      dependencies.screen = { disabled: false };
      dependencies.holiday = { disabled: false };
      dependencies.contractfuturesequivalent = { disabled: false };
    }

    // Apply singlDiff dependencies
    if (formData.singlDiff === "Single") {
      dependencies.contract1 = { disabled: true };
      dependencies.contract2 = { disabled: true };
      dependencies.holiday = { disabled: false };
    } else if (formData.singlDiff === "Diff") {
      dependencies.contract1 = { disabled: false };
      dependencies.contract2 = { disabled: false };
      dependencies.holiday = { disabled: true };
    }

    // Apply majorMini dependencies
    if (formData.majorMini === "Major") {
      dependencies.majorMiConn = { disabled: true };
    } else if (formData.majorMini === "Mini") {
      dependencies.majorMiConn = { disabled: false };
    }

    // Apply fullBal dependencies
    if (formData.fullBal === "Bal Month") {
      dependencies.balMontConn = { disabled: false };
    } else {
      dependencies.balMontConn = { disabled: true };
    }

    return dependencies;
  };

  // Updated useEffect
  useEffect(() => {
    console.log("useffect copied data ");
    if (!copiedData) return;

    const map = {
      derivative: copiedData.derivatives,
      singlDiff: copiedData.single_diff,
      majorMini: copiedData.major_mini,
      fullBal: copiedData.full_bal_month,
      contract: copiedData.contract_name,
      contract1: copiedData.contract1,
      contract2: copiedData.contract2,
      majorMiConn: copiedData.major_mini_connection,
      balMontConn: copiedData.bal_month_connection,
      unit: copiedData.unit,
      tick: copiedData.tick != null ? copiedData.tick.toString() : null,
      holiday: copiedData.holiday,
      bblConv:
        copiedData.kbbl_conversion_value != null
          ? copiedData.kbbl_conversion_value.toString()
          : null,
      priceConversionValue:
        copiedData.price_conversion != null
          ? copiedData.price_conversion.toString()
          : null,
      fwMonth:
        copiedData.f_w_months != null ? copiedData.f_w_months.toString() : null,
      ExchangeFees:
        copiedData.exchange_fees != null
          ? copiedData.exchange_fees.toString()
          : null,
      ExchangeClearanceFees:
        copiedData.exchange_clearance_fees != null
          ? copiedData.exchange_clearance_fees.toString()
          : null,
      exchangefeeBlocked:
        copiedData.exchange_fees_blocked != null
          ? copiedData.exchange_fees_blocked.toString()
          : null,
      exchangeClearanceFeesBlocked:
        copiedData.exchange_clearance_fees_blocked != null
          ? copiedData.exchange_clearance_fees_blocked.toString()
          : null,
      screen:
        copiedData.screen_fees != null
          ? copiedData.screen_fees.toString()
          : null,
      iceClearEuScreenFee:
        copiedData.ice_clearer_eu_screen_fees != null
          ? copiedData.ice_clearer_eu_screen_fees.toString()
          : null,
      iceClearEuBlockFee:
        copiedData.ice_clearer_eu_block_fee != null
          ? copiedData.ice_clearer_eu_block_fee.toString()
          : null,
      cashSettle:
        copiedData.cash_settle !== null
          ? copiedData.cash_settle.toString()
          : null,
      gmifcCode: copiedData.gimfc_code,
      physical: copiedData.physical_code,
      logical: copiedData.logical_code,
      symbol: copiedData.symbol_code,
      plattsCode: copiedData.platts_code,
      contractfuturesequivalent: copiedData.futures_equivalent_contract,
    };

    const updatedFormData = { ...formData };

    // First, update form data without touching validation
    Object.entries(map).forEach(([key, value]) => {
      if (value !== null && value !== "") {
        updatedFormData[key as keyof typeof formData] = value;
      }
    });
    if (updatedFormData.fullBal !== "Bal Month") {
      updatedFormData.balMontConn = "";
    }

    // Set the form data
    setFormData(updatedFormData);
    setIsFormModified(true);
    // Calculate and set field dependencies based on the updated form data
    const newDependencies = calculateFieldDependencies(updatedFormData);
    setFieldDependencies(newDependencies);

    // Clear any existing errors and touched state when copying new data
    // This prevents showing validation errors for fields that were populated via copy
    setTouched({});
    setErrors({});
  }, [copiedData]);

  const [fieldDependencies, setFieldDependencies] =
    useState<FieldDependenciesType>({
      contractfuturesequivalent: {
        disabled: true,
      },
      derivative: { disabled: false },
      singlDiff: { disabled: false },
      majorMini: { disabled: false },
      fullBal: { disabled: false },
      contract1: { disabled: false },
      contract2: { disabled: false },
      majorMiConn: { disabled: false },
      balMontConn: { disabled: false },
      ExchangeFees: { disabled: false },
      ExchangeClearanceFees: { disabled: false },
      iceClearEuBlockFee: { disabled: false },
      screen: { disabled: false },
      holiday: { disabled: false },
    });

  const fetchBalMontConnOptions = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/contract-full-bal-month/`
      );
      const balMontConnList = response.data; // Adjust based on API response structure
      console.log("balMontConnList...... BAL", balMontConnList);
      const formattedOptions = balMontConnList.map((item: any) => ({
        value: item.id.toString(), // Adjust based on API response field (e.g., item.id)
        label: item.name || item.contract_name, // Adjust based on API response field
      }));

      setBalMontConnOptions(formattedOptions);
    } catch (error) {
      toast.error(
        `An error occurred while fetching Bal Month Connection options: ${error}`
      );
    }
  };
  const fetchMajorMiConnOptions = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/contract-major-mini/`
      );
      const majorMiConnList = response.data; // Adjust based on API response structure
      console.log("majorMiConnList----Major", majorMiConnList);
      const formattedOptions = majorMiConnList.map((item: any) => ({
        value: item.id.toString(), // Adjust based on API response field (e.g., item.id)
        label: item.name || item.contract_name, // Adjust based on API response field
      }));
      setMajorMiConnOptions(formattedOptions);
    } catch (error) {
      toast.error(
        `An error occurred while fetching Major Mini Connection options: ${error}`
      );
    }
  };
  const unitOptionApi = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/units/`
      );
      const unitoptionsList = response.data.results;
      const unionOption = unitoptionsList.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setUnitOptions(unionOption);
    } catch (error) {
      toast.error(`an error occoured !! ${error}`);
    }
  };

  const holidayOptionApi = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/contract-holidays/forms/`
      );
      const holidayOptionList = response.data;
      const holidayOptionFormat = holidayOptionList.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setHolidayOption(holidayOptionFormat);
    } catch (error) {
      toast.error(`an error occoured !! ${error}`);
    }
  };

  const contractsOptionApi = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/contract-contracts/`
      );
      const contractsOptionList = response.data;
      console.log("contractsOptionList ", contractsOptionList);
      const contractsFormat = contractsOptionList.map((item: any) => ({
        value: item.id,
        label: item.contract_name,
      }));
      setContracts(contractsFormat);
      setBalMontOptions(contractsFormat);
    } catch (error) {
      toast.error(`an error occoured !! ${error}`);
    }
  };

  // Fetch Futures Equivalent Contract Options
  const fetchFuturesEquivalentOptions = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/contract-futures-equivalent/`
      );
      const futuresEquivalentList = response.data; // Adjust based on API response structure
      console.log("futuresEquivalentList", futuresEquivalentList);
      const formattedOptions = futuresEquivalentList.map((item: any) => ({
        value: item.id.toString(), // Adjust based on API response field
        label: item.name || item.contract_name, // Adjust based on API response field
      }));
      setContractFuturesEquivalentOptions(formattedOptions);
    } catch (error) {
      toast.error(
        `An error occurred while fetching futures equivalent contracts: ${error}`
      );
    }
  };

  useEffect(() => {
    fetchBalMontConnOptions();
    fetchMajorMiConnOptions();
    holidayOptionApi();
    unitOptionApi();
    contractsOptionApi();
    fetchFuturesEquivalentOptions();
  }, [dataVersion]);

  const validateField = (field: keyof FormDataType, value: string): string => {
    const floatFields = [
      "bblConv",
      "priceConversionValue",
      "ExchangeFees",
      "ExchangeClearanceFees",
      "exchangefeeBlocked",
      "exchangeClearanceFeesBlocked",
      "screen",
      "iceClearEuScreenFee",
      "iceClearEuBlockFee",
      "cashSettle"
    ];
    const integerFields = ["tick", "fwMonth"];
    const requiredFields = [
      "derivative",
      "singlDiff",
      "majorMini",
      "fullBal",
      "contract",
      "contract1",
      "contract2",
      "majorMiConn",
      "balMontConn",
      "unit",
      "holiday",
      "gmifcCode",
      "physical",
      "logical",
      "symbol",
      "plattsCode",
      "unit",
      "tick",
      "fwMonth",
      "cashSettle",
      "bblConv",
      "priceConversionValue",
      "ExchangeFees",
      "ExchangeClearanceFees",
      "exchangefeeBlocked",
      "exchangeClearanceFeesBlocked",
      "screen",
      "iceClearEuScreenFee",
      "iceClearEuBlockFee",
    ];
    const conditionalRequiredFields = {
      contract1: formData.singlDiff === "Diff",
      contract2: formData.singlDiff === "Diff",
      majorMiConn: formData.majorMini === "Mini",
      balMontConn: formData.fullBal === "Bal Month",
      ExchangeFees: formData.derivative === "futures",
      ExchangeClearanceFees: formData.derivative === "futures",
      // contractfuturesequivalent: formData.derivative === "swaps",
    };

    if (fieldDependencies[field]?.disabled) {
      return "";
    }

    if (
      requiredFields.includes(field as string) &&
      (!value || value.trim() === "")
    ) {
      return `${field} is required`;
    }

    if (
      conditionalRequiredFields[
        field as keyof typeof conditionalRequiredFields
      ] &&
      (!value || value.trim() === "")
    ) {
      return `${field} is required`;
    }

    if (floatFields.includes(field as string) && value && value.trim() !== "") {
      const error = validateNonZeroFloat (field as string, value);
      if (error) return error;
    }

    if (integerFields.includes(field as string) && value && value.trim() !== "") {
      const error = validateNonZeroInteger (field as string, value);
      if (error) return error;
    }
    if (field === "fwMonth" && Number(value) > 12) {
      return "F.W Months cannot be greater than 12";
    }
    if (field === "contract" && formData.singlDiff === "Diff" && value) {
      if (!/^.+ vs .+$/.test(value.trim())) {
        return `Contract must be in format "Contract1 vs Contract2"`;
      }
    }

    return "";
  };

  const validateAllFields = (): boolean => {
    const newErrors: ErrorsType = {};
    let isValid = true;

    Object.keys(formData).forEach((field) => {
      const fieldValue =
        typeof formData[field] === "string" ? (formData[field] as string) : "";
      const error = validateField(field as keyof FormDataType, fieldValue);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    console.log(newErrors);

    setErrors(newErrors);
    return isValid;
  };

  const handleBlur = (field: keyof FormDataType) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const fieldValue =
      typeof formData[field] === "string" ? (formData[field] as string) : "";
    const error = validateField(field, fieldValue);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleTextChange =
    (field: keyof FormDataType) => (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setFormData({
        ...formData,
        [field]: newValue,
      });
      setIsFormModified(true);
      const error = validateField(field, newValue);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    applyFieldDependencies(field, newValue);
  };

  const handleSelectChange =
    (field: keyof FormDataType) =>
    (eventOrValue: SelectChangeEvent | string) => {
      // Get value
      const newValue =
        typeof eventOrValue === "string"
          ? eventOrValue
          : eventOrValue.target.value;

      let updatedFormData = { ...formData, [field]: newValue };
      const currentSinglDiff = field === "singlDiff" ? newValue : formData.singlDiff;

      if (currentSinglDiff === "Diff") {
        const c1 = field === "contract1" ? newValue : formData.contract1;
        const c2 = field === "contract2" ? newValue : formData.contract2;
        const c1Label =
          (contractsOption as OptionType[]).find((o) => String(o.value) === String(c1))?.label || c1;
        const c2Label =
          (contractsOption as OptionType[]).find((o) => String(o.value) === String(c2))?.label || c2;
        if (c1 && c2) {
          updatedFormData.contract = `${c1Label} vs ${c2Label}`;
        } else {
          updatedFormData.contract = "";
        }
      }

      setFormData(updatedFormData);
      setIsFormModified(true);

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }

      applyFieldDependencies(field, newValue);
    };

  const applyFieldDependencies = (
    changedField: keyof FormDataType,
    value: any
  ) => {
    const newDependencies = { ...fieldDependencies };
    let newFormData = { ...formData };

    if (changedField === "derivative") {
      if (value === "futures") {
        newFormData.singlDiff = "Single";
        newFormData.derivative = "futures";
        newFormData.majorMini = "Major";
        newFormData.fullBal = "Full Month";
        newFormData.ExchangeFees = "";
        newFormData.ExchangeClearanceFees = "";
        newDependencies.contract1 = { disabled: true };
        newDependencies.contract2 = { disabled: true };
        newDependencies.majorMiConn = { disabled: true };
        newDependencies.balMontConn = { disabled: true };
        newDependencies.ExchangeFees = { disabled: false };
        newDependencies.iceClearEuScreenFee = { disabled: true };
        newDependencies.iceClearEuBlockFee = { disabled: true };
        newDependencies.ExchangeClearanceFees = { disabled: false };
        newDependencies.screen = { disabled: true };
        newDependencies.holiday = { disabled: false };
        newFormData.contractfuturesequivalent = ""; // Clear value when switching to futures
        newDependencies.contractfuturesequivalent = { disabled: true }; // Disable for futures

        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      } else {
        newFormData.derivative = "swaps";
        newFormData.singlDiff = "";
        newFormData.majorMini = "";
        newFormData.fullBal = "";
        newDependencies.ExchangeFees = { disabled: true };
        newDependencies.ExchangeClearanceFees = { disabled: true };
        newDependencies.contract1 = { disabled: false };
        newDependencies.contract2 = { disabled: false };
        newDependencies.majorMiConn = { disabled: false };
        newDependencies.balMontConn = { disabled: false };
        newDependencies.iceClearEuScreenFee = { disabled: false };
        newDependencies.iceClearEuBlockFee = { disabled: false };
        newDependencies.screen = { disabled: false };
        newDependencies.holiday = { disabled: false };
        newDependencies.contractfuturesequivalent = { disabled: false }; // Enable for swaps
        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      }
    }
    if (changedField === "singlDiff") {
      if (value === "Single") {
        newFormData.singlDiff = "Single";
        newDependencies.contract1 = { disabled: true };
        newDependencies.contract2 = { disabled: true };
        newDependencies.holiday = { disabled: false };
        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      } else {
        newFormData.singlDiff = "Diff";
        newDependencies.contract1 = { disabled: false };
        newDependencies.contract2 = { disabled: false };
        newDependencies.holiday = { disabled: true };
        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      }
    }

    if (changedField === "majorMini") {
      if (value === "Major") {
        newFormData.majorMini = "Major";
        newDependencies.majorMiConn = { disabled: true };
        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      } else {
        newFormData.majorMini = "Mini";
        newDependencies.majorMiConn = { disabled: false };
        setFormData(newFormData);
        setFieldDependencies(newDependencies);
      }
    }

    if (changedField === "fullBal") {
      newFormData.fullBal = value;
      if (value === "Bal Month") {
        newDependencies.balMontConn = { disabled: false };
      } else {
        newDependencies.balMontConn = { disabled: true };
        newFormData.balMontConn = "";
      }

      setFormData(newFormData);
      setFieldDependencies(newDependencies);
    }
  };

  const derivativeOptions = [
    { value: "futures", label: "Futures" },
    { value: "swaps", label: "Swaps" },
  ];

  const singlDiffOptions = [
    { value: "Single", label: "Single" },
    { value: "Diff", label: "Diff" },
  ];

  const majorMiniOption = [
    { value: "Major", label: "Major" },
    { value: "Mini", label: "Mini" },
  ];

  const fullMonthBalmonthOption = [
    { value: "Full Month", label: "Full Month" },
    { value: "Bal Month", label: "Bal Month" },
    { value: "Daily", label: "Daily" },
  ];

  const mockOptions = (prefix: string, count = 5): OptionType[] => {
    return Array.from({ length: count }, (_, i) => ({
      value: `${prefix.toLowerCase()}_option${i + 1}`,
      label: `${prefix} Option ${i + 1}`,
    }));
  };

  const resetFormData = () => {
    const initialFormData = {
      derivative: "",
      singlDiff: "",
      majorMini: "",
      fullBal: "",
      contract: "",
      contract1: "",
      contract2: "",
      majorMiConn: "",
      balMontConn: "",
      unit: "",
      tick: "",
      holiday: "",
      bblConv: "",
      price_unit: "",
      priceConversionValue: "",
      fwMonth: "",
      ExchangeFees: "",
      ExchangeClearanceFees: "",
      exchangefeeBlocked: "",
      exchangeClearanceFeesBlocked: "",
      screen: "",
      iceClearEuScreenFee: "",
      iceClearEuBlockFee: "",
      cashSettle: "",
      gmifcCode: "",
      physical: "",
      logical: "",
      symbol: "",
      plattsCode: "",
      contractfuturesequivalent: "",
    };

    setFormData(initialFormData);
    setTouched({});
    setErrors({});
    setIsFormModified(false);

    setFieldDependencies({
      derivative: { disabled: false },
      singlDiff: { disabled: false },
      majorMini: { disabled: false },
      fullBal: { disabled: false },
      contract1: { disabled: false },
      contract2: { disabled: false },
      majorMiConn: { disabled: false },
      balMontConn: { disabled: false },
      ExchangeFees: { disabled: false },
      ExchangeClearanceFees: { disabled: false },
      iceClearEuBlockFee: { disabled: false },
      screen: { disabled: false },
      holiday: { disabled: false },
      contractfuturesequivalent: { disabled: true }, // Reset new field
    });
  };

  const handleClearForm = () => {
    resetFormData();
  };
  const SubmitFormdata = async (e: any) => {
    e.preventDefault();

    const allTouched: TouchedType = {};
    Object.keys(formData).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);
    console.log("📦 FormData before submit:", formData);

    if (!validateAllFields()) {
      toast.error("Please complete all required fields before submitting");
      return;
    }

    // ---- Type Validations ----
    const floatFields = [
      "bblConv",
      "priceConversionValue",
      "ExchangeFees",
      "ExchangeClearanceFees",
      "exchangefeeBlocked",
      "exchangeClearanceFeesBlocked",
      "screen",
      "iceClearEuScreenFee",
      "iceClearEuBlockFee",
      "cashSettle"
    ];
    const integerFields = ["tick", "fwMonth"];
    const stringFields = [
      "gmifcCode",
      "physical",
      "logical",
      "symbol",
      "plattsCode",
      "derivative",
      "fullBal",
      "singlDiff",
      "majorMini",
      "contract",
      "contract1",
      "contract2",
      "majorMiConn",
      "balMontConn",
      "holiday",
    ];

    for (const field of stringFields) {
      const value = formData[field];
      if (Array.isArray(value) || typeof value !== "string") {
        toast.error(`${field} must be a string`);
        return;
      }
    }

    for (const field of floatFields) {
      const value = formData[field];
      if (value && (typeof value !== "string" || isNaN(Number(value)))) {
        toast.error(`${field} must be a Number`);
        return;
      }
    }

    for (const field of integerFields) {
      const value = formData[field];
      if (value && (typeof value !== "string" || !/^\d+$/.test(value))) {
        toast.error(`${field} must be an Integer`);
        return;
      }
    }

    // ---- Business Rules ----
    if (formData.derivative.trim() === "") {
      toast.error("Derivative Type is required");
      return;
    }
    if (formData.singlDiff.trim() === "") {
      toast.error("Single/Diff is required");
      return;
    }
    if (formData.majorMini.trim() === "") {
      toast.error("Major/Mini is required");
      return;
    }
    if (formData.fullBal.trim() === "") {
      toast.error("Full Month/Bal Month is required");
      return;
    }
    if (formData.majorMini === "Mini" && formData.majorMiConn.trim() === "") {
      toast.error("Major Mi Connection is required when Mini is selected");
      return;
    }
    if (
      formData.fullBal === "Bal Month" &&
      formData.balMontConn.trim() === ""
    ) {
      toast.error(
        "Bal Month Connection is required when Bal Month is selected"
      );
      return;
    }
    if (formData.singlDiff === "Diff") {
      if (formData.contract1.trim() === "") {
        toast.error("Contract 1 is required when Diff is selected");
        return;
      }
      if (formData.contract2.trim() === "") {
        toast.error("Contract 2 is required when Diff is selected");
        return;
      }
    }

    // ---- API Call ----
    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/contracts/`,
        {
          derivatives: formData.derivative === "futures" ? "futures" : "swaps",
          single_diff: formData.singlDiff,
          major_mini: formData.majorMini,
          full_bal_month: formData.fullBal,
          contract_name: formData.contract,
          contract1: formData.contract1,
          contract2: formData.contract2,
          major_mini_connection: formData.majorMiConn,
          bal_month_connection: formData.fullBal === "Bal Month" ? formData.balMontConn : null,
          unit: formData.unit,
          holiday: formData.holiday,
          tick: formData.tick,
          kbbl_conversion_value: formData.bblConv,
          price_unit: formData.price_unit,
          price_conversion: formData.priceConversionValue,
          f_w_months: formData.fwMonth,
          exchange_fees: formData.ExchangeFees,
          exchange_clearance_fees: formData.ExchangeClearanceFees,
          exchange_fees_blocked: formData.exchangefeeBlocked,
          exchange_clearance_fees_blocked:
            formData.exchangeClearanceFeesBlocked,
          screen_fees: formData.screen,
          ice_clearer_eu_screen_fees: formData.iceClearEuScreenFee,
          ice_clearer_eu_block_fee: formData.iceClearEuBlockFee,
          cash_settle: formData.cashSettle,
          gimfc_code: formData.gmifcCode,
          physical_code: formData.physical,
          logical_code: formData.logical,
          symbol_code: formData.symbol,
          platts_code: formData.plattsCode,
          futures_equivalent_contract:
            formData.contractfuturesequivalent || null,
        }
      );

      if ([200, 201, 202, 203].includes(response.status)) {
        toast.success("✅ Contract created successfully!");
        setDataVersion(dataVersion + 1);
        resetFormData();
      }
    } catch (error: any) {
      console.error("❌ API Error:", error);

      // Extract API error response
      const apiError = error.response?.data;

      if (apiError?.errors?.non_field_errors?.length > 0) {
        toast.error(apiError.errors.non_field_errors[0]);
      } else if (apiError?.message) {
        toast.error(apiError.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <Accordion defaultActiveKey="0" className="table-ad__addInputs">
      <Accordion.Item eventKey="0">
        <Accordion.Header className="accordion_button">
          Add Contract
        </Accordion.Header>
        <Accordion.Body>
          <Box
            sx={{
              width: "100%",
              p: { xs: 1, sm: 1 },
              pt: "20px !important",
              backgroundColor: "#ffff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                "& > *": {
                  flex: "1 1 0px",
                  minWidth: {
                    xs: "100%",
                    sm: "48%",
                    md: "23%",
                    lg: "15%",
                    xl: "7.5%",
                  },
                },
              }}
            >
              {fieldDependencies.derivative?.disabled !== true && (
                <CompactSelect
                  label="Derivative"
                  value={formData.derivative}
                  onChange={handleSelectChange("derivative")}
                  options={derivativeOptions}
                  disabled={fieldDependencies.derivative?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.derivative && !!errors.derivative}
                  onBlur={() => handleBlur("derivative")}
                />
              )}
              {fieldDependencies.singlDiff?.disabled !== true && (
                <CompactSelect
                  label="Singl/Diff"
                  value={formData.singlDiff}
                  onChange={handleSelectChange("singlDiff")}
                  options={singlDiffOptions}
                  disabled={fieldDependencies.singlDiff?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.singlDiff && !!errors.singlDiff}
                  onBlur={() => handleBlur("singlDiff")}
                  sx={
                    formData.derivative === "futures"
                      ? { pointerEvents: "none" }
                      : {}
                  }
                />
              )}
              {fieldDependencies.majorMini?.disabled !== true && (
                <CompactSelect
                  label="Major/Mini"
                  value={formData.majorMini}
                  onChange={handleSelectChange("majorMini")}
                  options={majorMiniOption}
                  disabled={fieldDependencies.majorMini?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.majorMini && !!errors.majorMini}
                  onBlur={() => handleBlur("majorMini")}
                  sx={
                    formData.derivative === "futures"
                      ? { pointerEvents: "none" }
                      : {}
                  }
                />
              )}
              {fieldDependencies.fullBal?.disabled !== true && (
                <CompactSelect
                  label="Full/Bal"
                  value={formData.fullBal}
                  onChange={handleSelectChange("fullBal")}
                  options={fullMonthBalmonthOption}
                  disabled={fieldDependencies.fullBal?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.fullBal && !!errors.fullBal}
                  onBlur={() => handleBlur("fullBal")}
                  sx={
                    formData.derivative === "futures"
                      ? { pointerEvents: "none" }
                      : {}
                  }
                />
              )}
              <CompactTextField
                label="Contract"
                value={formData.contract}
                // onChange={handleSelectChange("contract")}
                onChange={handleTextChange("contract")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.contract && !!errors.contract}
                onBlur={() => handleBlur("contract")}
              />
              {fieldDependencies.contract1?.disabled !== true && (
                <CompactSelect
                  label="Contract 1"
                  value={formData.contract1}
                  onChange={handleSelectChange("contract1")}
                  options={contractsOption}
                  disabled={fieldDependencies.contract1?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.contract1 && !!errors.contract1}
                  onBlur={() => handleBlur("contract1")}
                />
              )}
              {fieldDependencies.contract2?.disabled !== true && (
                <CompactSelect
                  label="Contract 2"
                  value={formData.contract2}
                  onChange={handleSelectChange("contract2")}
                  options={contractsOption}
                  disabled={fieldDependencies.contract2?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.contract2 && !!errors.contract2}
                  onBlur={() => handleBlur("contract2")}
                />
              )}
              {fieldDependencies.contractfuturesequivalent?.disabled !==
                true && (
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    width: "100%", // will expand to parent width
                    minWidth: "200px", // ✅ ensures long label fits
                  }}
                >
                  <CompactSelect
                    label="Futures Equivalent Contract"
                    value={formData.contractfuturesequivalent || ""}
                    onChange={handleSelectChange("contractfuturesequivalent")}
                    options={contractFuturesEquivalentOptions}
                    disabled={
                      fieldDependencies.contractfuturesequivalent?.disabled
                    }
                    error={
                      touched.contractfuturesequivalent &&
                      !!errors.contractfuturesequivalent
                    }
                    onBlur={() => handleBlur("contractfuturesequivalent")}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiSelect-select": {
                        pr: "32px", // ✅ add padding-right so text doesn’t hide under clear icon
                      },
                    }}
                  />
                  {formData.contractfuturesequivalent && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSelectChange("contractfuturesequivalent")("")
                      }
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 4,
                        transform: "translateY(-50%)",
                        pointerEvents: "auto",
                        marginRight: "15px",
                        marginTop: "-5px",
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
              {fieldDependencies.majorMiConn?.disabled !== true && (
                <CompactSelect
                  label="Major Mini Connection"
                  value={formData.majorMiConn}
                  onChange={handleSelectChange("majorMiConn")}
                  options={majorMiConnOptions}
                  disabled={fieldDependencies.majorMiConn?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.majorMiConn && !!errors.majorMiConn}
                  onBlur={() => handleBlur("majorMiConn")}
                />
              )}
              {fieldDependencies.balMontConn?.disabled !== true && (
                <CompactSelect
                  label="Bal Month Connection"
                  value={formData.balMontConn}
                  onChange={handleSelectChange("balMontConn")}
                  options={balMontConnOptions}
                  disabled={fieldDependencies.balMontConn?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={touched.balMontConn && !!errors.balMontConn}
                  onBlur={() => handleBlur("balMontConn")}
                />
              )}
              <CompactSelect
                label="Unit"
                value={formData.unit}
                onChange={handleSelectChange("unit")}
                options={unitOption}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.unit && !!errors.unit}
                onBlur={() => handleBlur("unit")}
              />
              <CompactTextField
                label="Tick"
                value={formData.tick}
                type="number"
                onChange={handleTextChange("tick")}
                helperText={touched.tick ? errors.tick : ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.tick && !!errors.tick}
                onBlur={() => handleBlur("tick")}
              />
              <CompactSelect
                label="Holiday"
                value={formData.holiday}
                onChange={handleSelectChange("holiday")}
                options={holidayOption}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.holiday && !!errors.holiday}
                onBlur={() => handleBlur("holiday")}
              />
              <CompactTextField
                label="BBL Conversion Value"
                value={formData.bblConv}
                onChange={handleTextChange("bblConv")}
                type="number"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.bblConv && !!errors.bblConv}
                onBlur={() => handleBlur("bblConv")}
              />
              <CompactSelect
                label="Price Unit"
                value={formData.price_unit}
                onChange={handleSelectChange("price_unit")}
                options={unitOption}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.price_unit && !!errors.price_unit}
                onBlur={() => handleBlur("price_unit")}
              />
              <CompactTextField
                label="Price Conversion Value"
                type="number"
                value={formData.priceConversionValue}
                // onChange={handleSelectChange("priceConversionValue")}
                onChange={handleTextChange("priceConversionValue")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={
                  touched.priceConversionValue && !!errors.priceConversionValue
                }
                onBlur={() => handleBlur("priceConversionValue")}
              />
              <CompactTextField
                label="F.W Months"
                value={formData.fwMonth}
                type="number"
                // onChange={handleSelectChange("fwMonth")}
                onChange={handleTextChange("fwMonth")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                helperText={touched.fwMonth ? errors.fwMonth : ""}
                error={touched.fwMonth && !!errors.fwMonth}
                onBlur={() => handleBlur("fwMonth")}
              />
              {fieldDependencies.ExchangeFees?.disabled !== true && (
                <CompactTextField
                  label="Exchange Fees"
                  value={formData.ExchangeFees}
                  type="number"
                  // onChange={handleTextChange("ExchangeFees")}
                  onChange={handleTextChange("ExchangeFees")}
                  disabled={fieldDependencies.ExchangeFees?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  helperText={touched.ExchangeFees ? errors.ExchangeFees : ""}
                  error={touched.ExchangeFees && !!errors.ExchangeFees}
                  onBlur={() => handleBlur("ExchangeFees")}
                />
              )}
              {fieldDependencies.ExchangeClearanceFees?.disabled !== true && (
                <CompactTextField
                  label="Exchange Clearance Fees"
                  type="number"
                  value={formData.ExchangeClearanceFees}
                  // onChange={handleTextChange("ExchangeClearanceFees")}
                  onChange={handleTextChange("ExchangeClearanceFees")}
                  disabled={fieldDependencies.ExchangeClearanceFees?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  helperText={touched.ExchangeClearanceFees ? errors.ExchangeClearanceFees : ""}
                  error={
                    touched.ExchangeClearanceFees &&
                    !!errors.ExchangeClearanceFees
                  }
                  onBlur={() => handleBlur("ExchangeClearanceFees")}
                />
              )}
              <CompactTextField
                label="Exchange Fees Blocked"
                value={formData.exchangefeeBlocked}
                type="number"
                // onChange={handleTextChange("exchangefeeBlocked")}
                onChange={handleTextChange("exchangefeeBlocked")}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                helperText={touched.exchangefeeBlocked ? errors.exchangefeeBlocked : ""}
                error={
                  touched.exchangefeeBlocked && !!errors.exchangefeeBlocked
                }
                onBlur={() => handleBlur("exchangefeeBlocked")}
              />
              <CompactTextField
                label="Exchange clearance fees Blocked"
                value={formData.exchangeClearanceFeesBlocked}
                type="number"
                // onChange={handleTextChange("exchangeClearanceFeesBlocked")}
                onChange={handleTextChange("exchangeClearanceFeesBlocked")}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                helperText={touched.exchangeClearanceFeesBlocked ? errors.exchangeClearanceFeesBlocked : ""}
                error={
                  touched.exchangeClearanceFeesBlocked &&
                  !!errors.exchangeClearanceFeesBlocked
                }
                onBlur={() => handleBlur("exchangeClearanceFeesBlocked")}
              />
              {fieldDependencies.screen?.disabled !== true && (
                <CompactTextField
                  label="Screen Fees"
                  value={formData.screen}
                  type="number"
                  //onChange={handleSelectChange("screen")}
                  onChange={handleTextChange("screen")} // Changed to handleTextChange
                  disabled={fieldDependencies.screen?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  helperText={touched.screen ? errors.screen : ""}
                  error={touched.screen && !!errors.screen}
                  onBlur={() => handleBlur("screen")}
                />
              )}
              {fieldDependencies.iceClearEuScreenFee?.disabled !== true && (

              <CompactTextField
                label="ICE Clearer Eu Screen Fees"
                value={formData.iceClearEuScreenFee}
                type="number"
                //onChange={handleSelectChange("iceClearEuScreenFee")}
                onChange={handleTextChange("iceClearEuScreenFee")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                helperText={touched.iceClearEuScreenFee ? errors.iceClearEuScreenFee : ""}
                error={
                  touched.iceClearEuScreenFee && !!errors.iceClearEuScreenFee
                }
                onBlur={() => handleBlur("iceClearEuScreenFee")}
              />
              )}
              {fieldDependencies.iceClearEuBlockFee?.disabled !== true && (
                <CompactTextField
                  label="ICE Clearer EU Block Fees"
                  value={formData.iceClearEuBlockFee}
                  type="number"
                  //onChange={handleSelectChange("iceClearEuBlockFee")}
                  onChange={handleTextChange("iceClearEuBlockFee")} // Changed to handleTextChange
                  disabled={fieldDependencies.iceClearEuBlockFee?.disabled}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  helperText={touched.iceClearEuBlockFee ? errors.iceClearEuBlockFee : ""}
                  error={
                    touched.iceClearEuBlockFee && !!errors.iceClearEuBlockFee
                  }
                  onBlur={() => handleBlur("iceClearEuBlockFee")}
                />
              )}
              <CompactTextField
                label="Cash Settle"
                value={formData.cashSettle}
                type="number"
                //onChange={handleTextChange("cashSettle")}
                onChange={handleTextChange("cashSettle")}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.cashSettle && !!errors.cashSettle}
                onBlur={() => handleBlur("cashSettle")}
              />
              <CompactTextField
                label="GMIFC Code"
                value={formData.gmifcCode}
                //onChange={handleSelectChange("gmifcCode")}
                onChange={handleTextChange("gmifcCode")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.gmifcCode && !!errors.gmifcCode}
                onBlur={() => handleBlur("gmifcCode")}
              />
              <CompactTextField
                label="Physical Code"
                value={formData.physical}
                //onChange={handleSelectChange("physical")}
                onChange={handleTextChange("physical")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.physical && !!errors.physical}
                onBlur={() => handleBlur("physical")}
              />
              <CompactTextField
                label="Logical Code"
                value={formData.logical}
                //onChange={handleSelectChange("logical")}
                onChange={handleTextChange("logical")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.logical && !!errors.logical}
                onBlur={() => handleBlur("logical")}
              />
              <CompactTextField
                label="Symbol Code"
                value={formData.symbol}
                onChange={handleTextChange("symbol")}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.symbol && !!errors.symbol}
                onBlur={() => handleBlur("symbol")}
              />
              <CompactTextField
                label="Platts Code"
                value={formData.plattsCode}
                //onChange={handleSelectChange("plattsCode")}
                onChange={handleTextChange("plattsCode")} // Changed to handleTextChange
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                error={touched.plattsCode && !!errors.plattsCode}
                onBlur={() => handleBlur("plattsCode")}
              />
              <Button
                onClick={SubmitFormdata}
                variant="contained"
                fullWidth
                sx={{
                  height: { xs: "36px", sm: "32px" },
                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                  minWidth: { xs: "100%", sm: "100px" },
                  maxWidth: { sm: "150px" },
                  backgroundColor: "#00bcd4",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  "&:hover": {
                    backgroundColor: "#008c9e",
                  },
                }}
              >
                Add
              </Button>
              <Button
                onClick={handleClearForm}
                variant="outlined"
                fullWidth
                disabled={!isFormModified}
                sx={{
                  height: { xs: "36px", sm: "32px" },
                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                  minWidth: { xs: "100%", sm: "100px" },
                  maxWidth: { sm: "150px" },
                  marginLeft: { sm: "8px" },
                  textTransform: "none",
                  fontSize: "0.75rem",
                  borderColor: "#e0e0e0",
                  color: isFormModified ? "#222" : "#999",
                  backgroundColor: isFormModified ? "#e6f3f5" : "#f5f5f5",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    borderColor: isFormModified ? "#008c9e" : "#e0e0e0",
                    backgroundColor: isFormModified ? "#d1e8eb" : "#f5f5f5",
                    transform: isFormModified ? "scale(1.02)" : "none",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e0e0e0",
                    backgroundColor: "#f5f5f5",
                    color: "#999",
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default CompactForm;
